/**
 * Validation utilities for reporting/dashboard data.
 *
 * This module provides client-side validation that mirrors the backend validation
 * logic, ensuring consistent rules and enabling real-time feedback to users.
 *
 * The validation rules are derived from the metric configuration (data_value_type,
 * boundary_direction, level_calculation_mode) to maintain a single source of truth.
 */

import { DataValueType, BoundaryDirection, LevelCalculationMode, Level } from '../api/types';

// ==================== Types ====================

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

export interface DataValueValidationRules {
  allowNegative: boolean;
  minValue: number | null;
  maxValue: number | null;
}

export interface ThresholdValidationRules {
  expectedOrder: 'ascending' | 'descending';
  levelOrder: Level[];
}

// ==================== Constants ====================

// Level order from "best" to "worst" for threshold comparison
const LEVEL_ORDER: Level[] = ['high', 'substantial', 'moderate', 'limited', 'none'];

// Optional minus, digits, optional single decimal point with digits.
// Allows: "3", "3.5", "-3.5", "3." (user still typing). Rejects: "50abc", "3.5.6".
const VALID_NUMBER_FORMAT = /^-?\d+(?:\.\d*)?$/;

// ==================== Data Value Validation ====================

/**
 * Get validation rules for data values based on metric configuration.
 * This mirrors the backend logic to provide instant feedback.
 */
export function getDataValueRules(
  dataValueType: DataValueType | null,
  _boundaryDirection?: BoundaryDirection | null,
  levelCalculationMode?: LevelCalculationMode | null
): DataValueValidationRules {
  const isDeviationMode = levelCalculationMode === 'deviation_bands' || levelCalculationMode === 'deviation_direct';

  if (dataValueType === 'percentage' && !isDeviationMode) {
    // Only enforce 0-100 for absolute mode. In deviation modes the raw value
    // is an unrestricted number; the percentage is computed relative to the base.
    return {
      allowNegative: false,
      minValue: 0,
      maxValue: 100
    };
  }

  // For number type, or any deviation mode, allow any value
  return {
    allowNegative: true,
    minValue: null,
    maxValue: null
  };
}

/**
 * Validate a data value against the rules.
 */
export function validateDataValue(value: number, rules: DataValueValidationRules): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!rules.allowNegative && value < 0) {
    errors.push({
      field: 'value',
      message: 'Value cannot be negative for percentage metrics',
      code: 'negative_value'
    });
  }

  if (rules.minValue !== null && value < rules.minValue) {
    errors.push({
      field: 'value',
      message: `Value must be at least ${rules.minValue}`,
      code: 'below_minimum'
    });
  }

  if (rules.maxValue !== null && value > rules.maxValue) {
    errors.push({
      field: 'value',
      message: `Value must be at most ${rules.maxValue}`,
      code: 'above_maximum'
    });
  }

  return errors;
}

/**
 * Validate a data value string input.
 * Returns errors if the value is invalid, empty array if valid.
 */
export function validateDataValueInput(
  inputValue: string,
  dataValueType: DataValueType | null,
  boundaryDirection?: BoundaryDirection | null,
  levelCalculationMode?: LevelCalculationMode | null
): ValidationError[] {
  const trimmed = inputValue.trim();

  // Empty is valid (just means nothing entered yet)
  if (!trimmed) {
    return [];
  }

  if (!VALID_NUMBER_FORMAT.test(trimmed)) {
    return [
      {
        field: 'value',
        message: 'Please enter a valid number',
        code: 'invalid_number'
      }
    ];
  }

  const numValue = parseFloat(trimmed);
  if (isNaN(numValue)) {
    return [
      {
        field: 'value',
        message: 'Please enter a valid number',
        code: 'invalid_number'
      }
    ];
  }

  const rules = getDataValueRules(dataValueType, boundaryDirection, levelCalculationMode);
  return validateDataValue(numValue, rules);
}

// ==================== Threshold Validation ====================

/**
 * Get validation rules for threshold ordering.
 */
export function getThresholdValidationRules(
  boundaryDirection: BoundaryDirection | null,
  calculationMode: LevelCalculationMode | null
): ThresholdValidationRules {
  // Determine expected ordering
  let expectedOrder: 'ascending' | 'descending';

  if (calculationMode === 'deviation_bands') {
    // For deviation bands, lower deviation = better, so ascending order
    expectedOrder = 'ascending';
  } else if (boundaryDirection === 'ascending') {
    // Lower is better, so thresholds should increase: High < Substantial < ...
    expectedOrder = 'ascending';
  } else {
    // Higher is better (descending), so thresholds should decrease: High > Substantial > ...
    expectedOrder = 'descending';
  }

  return {
    expectedOrder,
    levelOrder: LEVEL_ORDER
  };
}

/**
 * Helper function to capitalize first letter
 */
function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Check if thresholds are in the correct order.
 *
 * For descending direction (higher is better):
 *   High ≥ Substantial ≥ Moderate ≥ Limited ≥ None
 *
 * For ascending direction (lower is better):
 *   High ≤ Substantial ≤ Moderate ≤ Limited ≤ None
 *
 * For deviation bands (always ascending - tighter bands = better):
 *   High ≤ Substantial ≤ Moderate ≤ Limited ≤ None
 */
export function validateThresholdOrdering(
  thresholds: Record<Level, string>,
  boundaryDirection: BoundaryDirection | null,
  calculationMode: LevelCalculationMode | null
): ValidationError[] {
  const errors: ValidationError[] = [];

  const rules = getThresholdValidationRules(boundaryDirection, calculationMode);
  const shouldBeAscending = rules.expectedOrder === 'ascending';

  // Get threshold values in level order (High to None)
  const values: Array<{ level: Level; value: number }> = [];
  for (const level of LEVEL_ORDER) {
    const numValue = parseThresholdInput(thresholds[level]);
    if (numValue !== null) {
      values.push({ level, value: numValue });
    }
  }

  const coveredPairs = new Set<string>();
  for (let i = 1; i < values.length - 1; i++) {
    const prev = values[i - 1];
    const curr = values[i];
    const next = values[i + 1];
    const lo = Math.min(prev.value, next.value);
    const hi = Math.max(prev.value, next.value);
    if (!(lo <= curr.value && curr.value <= hi)) {
      errors.push({
        field: `threshold_${curr.level}`,
        message: `${capitalize(curr.level)} (${curr.value}) must sit between ${capitalize(prev.level)} (${prev.value}) and ${capitalize(next.level)} (${next.value})`,
        code: 'unordered_thresholds'
      });
      coveredPairs.add(`${prev.level}:${curr.level}`);
      coveredPairs.add(`${curr.level}:${next.level}`);
    }
  }

  // Check ordering
  for (let i = 0; i < values.length - 1; i++) {
    const curr = values[i];
    const next = values[i + 1];
    if (coveredPairs.has(`${curr.level}:${next.level}`)) {
      continue;
    }

    if (shouldBeAscending) {
      if (curr.value > next.value) {
        errors.push({
          field: `threshold_${next.level}`,
          message: `${capitalize(next.level)} (${next.value}) must be ≥ ${capitalize(curr.level)} (${curr.value})`,
          code: 'unordered_thresholds'
        });
      }
    } else {
      if (curr.value < next.value) {
        errors.push({
          field: `threshold_${next.level}`,
          message: `${capitalize(next.level)} (${next.value}) must be ≤ ${capitalize(curr.level)} (${curr.value})`,
          code: 'unordered_thresholds'
        });
      }
    }
  }

  return errors;
}

export function parseThresholdInput(strValue: string | undefined): number | null {
  if (!strValue || strValue.trim() === '') {
    return null;
  }
  const trimmed = strValue.trim();
  if (!VALID_NUMBER_FORMAT.test(trimmed)) {
    return null;
  }
  const numValue = parseFloat(trimmed);
  return Number.isFinite(numValue) ? numValue : null;
}

function validateThresholdFormats(thresholds: Record<Level, string>): ValidationError[] {
  const errors: ValidationError[] = [];
  for (const level of LEVEL_ORDER) {
    if (level === 'none') {
      continue;
    }
    const strValue = thresholds[level];
    if (!strValue || strValue.trim() === '') {
      continue;
    }
    if (parseThresholdInput(strValue) === null) {
      errors.push({
        field: `threshold_${level}`,
        message: 'Please enter a valid number',
        code: 'invalid_number'
      });
    }
  }
  return errors;
}

export function validateNoneIsCatchall(thresholds: Record<Level, string>): ValidationError[] {
  const raw = thresholds.none;
  if (!raw || raw.trim() === '') {
    return [];
  }
  return [
    {
      field: 'threshold_none',
      message:
        "Level 'none' must have threshold_value=null (catch-all). Do not give it a numeric sentinel like 0 or 999.",
      code: 'numeric_none_threshold'
    }
  ];
}

/**
 * Reject a blank threshold sandwiched between populated thresholds.
 *
 * Trailing blanks are allowed — None is the catch-all — and an entirely
 * empty set is allowed (no numeric bands yet).
 */
export function validateThresholdGaps(thresholds: Record<Level, string>): ValidationError[] {
  const errors: ValidationError[] = [];
  const orderedValues = LEVEL_ORDER.map((level) => parseThresholdInput(thresholds[level]));
  let lastNonNullIndex = -1;
  for (let idx = 0; idx < orderedValues.length; idx++) {
    if (orderedValues[idx] !== null) {
      lastNonNullIndex = idx;
    }
  }
  if (lastNonNullIndex < 0) {
    return errors;
  }
  for (let idx = 0; idx < lastNonNullIndex; idx++) {
    if (orderedValues[idx] === null) {
      const level = LEVEL_ORDER[idx];
      errors.push({
        field: `threshold_${level}`,
        message: `${capitalize(level)} threshold cannot be empty when a lower band has a value. Fill every level down to your catch-all, or clear all thresholds.`,
        code: 'threshold_gap'
      });
    }
  }
  return errors;
}

/**
 * Deviation thresholds are magnitudes: band widths ≥ 0, percent of base > 0.
 */
export function validateThresholdValuesForMode(
  thresholds: Record<Level, string>,
  calculationMode: LevelCalculationMode | null
): ValidationError[] {
  if (calculationMode !== 'deviation_bands' && calculationMode !== 'deviation_direct') {
    return [];
  }
  const isBands = calculationMode === 'deviation_bands';
  const kind = isBands ? 'band width' : 'percent of base';
  const bound = isBands ? '≥ 0' : 'greater than 0';
  const errors: ValidationError[] = [];
  for (const level of LEVEL_ORDER) {
    if (level === 'none') {
      continue;
    }
    const value = parseThresholdInput(thresholds[level]);
    if (value === null) {
      continue;
    }
    const invalid = isBands ? value < 0 : value <= 0;
    if (invalid) {
      errors.push({
        field: `threshold_${level}`,
        message: `${capitalize(level)} ${kind} must be ${bound} (got ${value})`,
        code: 'non_positive_threshold'
      });
    }
  }
  return errors;
}

/**
 * Ordering, mid-list gaps, and mode-specific value checks for a boundary set.
 */
export function validateLevelBoundaries(
  thresholds: Record<Level, string>,
  boundaryDirection: BoundaryDirection | null,
  calculationMode: LevelCalculationMode | null
): ValidationError[] {
  return [
    ...validateThresholdFormats(thresholds),
    ...validateThresholdOrdering(thresholds, boundaryDirection, calculationMode),
    ...validateThresholdGaps(thresholds),
    ...validateNoneIsCatchall(thresholds),
    ...validateThresholdValuesForMode(thresholds, calculationMode)
  ];
}

/**
 * Get a human-readable description of the expected threshold order.
 */
export function getThresholdOrderDescription(
  boundaryDirection: BoundaryDirection | null,
  calculationMode: LevelCalculationMode | null
): string {
  const rules = getThresholdValidationRules(boundaryDirection, calculationMode);

  if (rules.expectedOrder === 'ascending') {
    return 'Thresholds should increase from High to None (High ≤ Substantial ≤ Moderate ≤ Limited ≤ None)';
  }
  return 'Thresholds should decrease from High to None (High ≥ Substantial ≥ Moderate ≥ Limited ≥ None)';
}

// ==================== Fiscal Quarters ====================

/**
 * Get quarter number from month (fiscal year starting April).
 * Apr-Jun = Q1, Jul-Sep = Q2, Oct-Dec = Q3, Jan-Mar = Q4
 */
export function getQuarterFromMonth(month: number): number {
  return Math.floor(((month + 8) % 12) / 3) + 1;
}

/**
 * Quarter-end (representative) months for quarterly cards, indexed by
 * quarter-1: Q1→Jun(6), Q2→Sep(9), Q3→Dec(12), Q4→Mar(3). Mirrors the
 * backend's QUARTER_END_MONTHS in period_utils.py.
 */
export const QUARTER_END_MONTHS = [6, 9, 12, 3] as const;
